/**
 * Contract for the "Refresh company profile" endpoint — used by both
 * the welcome wizard (during onboarding) and Settings ("re-run
 * enrichment now") to spawn the company-profile-filler worker.
 *
 *   POST /api/onboarding/refresh-profile  { domain: string }
 *
 * Returns the worker_run_id so the caller can poll
 * /api/onboarding/refresh-profile/status?since=ISO for completion.
 *
 *   GET  /api/onboarding/refresh-profile/status?since=ISO
 *
 * Phase C scaffolding (per docs/onboarding/INPUT-REDUCTION-AUDIT.md
 * §6.3 + Wave 1 of the production-readiness pass).
 */

import { z } from "zod";
import type { Operation } from "../types";

export const refreshProfileBody = z.object({
  domain: z
    .string()
    .min(3)
    .describe("Company domain — normalized server-side."),
});
export type RefreshProfileBody = z.infer<typeof refreshProfileBody>;

export const refreshProfileResponse = z.object({
  ok: z.literal(true),
  worker_run_id: z.number().nullable(),
  started_at: z.string(),
  domain_normalized: z.string(),
  spawned_count: z.number(),
  reason: z.string().nullable(),
});
export type RefreshProfileResponse = z.infer<typeof refreshProfileResponse>;

export const refreshProfileStatusQuery = z.object({
  since: z.string().optional(),
});

export const refreshProfileStatusResponse = z.object({
  worker_runs: z.array(
    z.object({
      worker_run_id: z.number(),
      role: z.string(),
      section: z.string().nullable(),
      status: z.enum(["running", "completed", "failed", "budget_exceeded", "aborted"]),
      started_at: z.string(),
      finished_at: z.string().nullable(),
    }),
  ),
  total: z.number(),
  completed: z.number(),
  failed: z.number(),
});
export type RefreshProfileStatusResponse = z.infer<
  typeof refreshProfileStatusResponse
>;

export const onboardingRefresh = {
  run: {
    method: "POST",
    path: () => "/api/onboarding/refresh-profile",
    body: refreshProfileBody,
    response: refreshProfileResponse,
  } satisfies Operation<void, void, RefreshProfileBody, RefreshProfileResponse>,
  status: {
    method: "GET",
    path: () => "/api/onboarding/refresh-profile/status",
    query: refreshProfileStatusQuery,
    response: refreshProfileStatusResponse,
  } satisfies Operation<
    void,
    z.infer<typeof refreshProfileStatusQuery>,
    void,
    RefreshProfileStatusResponse
  >,
};
